// API types matching the backend response format (snake_case)

export interface CategoryEntry {
  slug: string;
  title: string;
  icon: string;
  description: string;
  dir_path: string;
  doc_count: number;
}

export interface DocEntry {
  slug: string;
  title: string;
  date: string;
  tags: string[];
  category: string;
  cover: string | null;
  summary: string;
  status: string;
  file_path: string;
}

// 详情响应（后端返回完整字段，含 content 与 version）
export interface DocDetail extends DocEntry {
  content: string;
  version: number;
}

// 版本完整快照（含 content，对应 GET /api/docs/{slug}/versions/{version}）
export interface DocVersionDetail {
  version: number;
  title: string;
  category: string;
  summary: string;
  status: string;
  tags: string[];
  content: string;
  actor: string;
  created_at: string;
}

export interface CreateDocRequest {
  title: string;
  category: string;
  tags?: string[];
  status?: string;
  content: string;
}

export interface UpdateDocRequest {
  title?: string;
  tags?: string[];
  status?: string;
  content?: string;
}

// 操作审计日志条目（对应 GET /api/audit-logs 返回格式）
export interface AuditLog {
  doc_slug: string;
  doc_title: string;
  action: string;
  actor: string;
  version: number | null;
  description: string;
  created_at: string;
}

// ── Cyberpunk Gallery ──────────────────────────────────────────

// 变体条目（服务端按质量档生成的多份压缩图）
export interface PhotoVariant {
  cos_key: string;
  cos_url: string;
  mime_type: string;
  size_bytes: number;
}

// 画廊照片日记（对应 GET /api/photos 返回格式，COS 存储）
export interface CyberpunkPhoto {
  id: number;
  cos_key: string;
  cos_url: string;
  filename: string;
  title: string;
  description: string;          // 日记正文
  diary_date: string | null;    // 日记日期（YYYY-MM-DD），null 回退 created_at
  width: number;
  height: number;
  size_bytes: number;
  mime_type: string;
  sort_order: number;
  status: string;   // active | hidden | deleted
  source: string;   // manual | mcp（MCP 自动处理预留）
  uploaded_by: string;
  /** 多质量变体 {"100": {...}, "40": {...}}（键=质量档）；老记录缺省，回退 cos_url */
  variants?: Record<string, PhotoVariant>;
  created_at: string;
  updated_at: string;
}

/** 列表小图：取最小质量档变体，无变体（老记录/动图）回退原图 */
export function thumbUrl(photo: CyberpunkPhoto): string {
  return pickVariant(photo, 'min');
}

/** 点开大图：取最大质量档变体，无变体回退原图 */
export function fullUrl(photo: CyberpunkPhoto): string {
  return pickVariant(photo, 'max');
}

function pickVariant(photo: CyberpunkPhoto, which: 'min' | 'max'): string {
  const variants = photo.variants;
  if (!variants) return photo.cos_url;
  const tiers = Object.keys(variants)
    .filter((k) => /^\d+$/.test(k))
    .map(Number)
    .sort((a, b) => a - b);
  if (tiers.length === 0) return photo.cos_url;
  const key = String(which === 'min' ? tiers[0] : tiers[tiers.length - 1]);
  return variants[key]?.cos_url || photo.cos_url;
}

export interface UploadPhotoRequest {
  filename: string;
  content_type: string;
  data_base64: string;
  title?: string;
  description?: string;
  diary_date?: string;          // 缺省为上传当天
  width?: number;
  height?: number;
  ratios?: number[];            // 变体质量档 1..=100（1..4 个），缺省 [100, 40]
  webp?: boolean;               // 是否转 webp，缺省 true
}

export interface UpdatePhotoRequest {
  title?: string;
  description?: string;
  diary_date?: string;          // 空串 = 清空日期
  sort_order?: number;
  status?: string;
}
